
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function StepsSection() {
  const { t } = useTranslation('method');
  const [activeStep, setActiveStep] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.15 });
  const { ref: navRef, isVisible: navVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: detailRef, isVisible: detailVisible } = useScrollReveal({ threshold: 0.1 });

  const steps = t('steps', { returnObjects: true }) as Array<{
    number: string;
    title: string;
    description: string;
    details: string[];
    duration: string;
    deliverable: string;
  }>;

  return (
    <section className="py-28 px-6 bg-[#0B0B0B]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-20">
          <p
            className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F97700] mb-5 transition-all duration-700"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: '0ms',
            }}
          >
            {t('stepsSection.label')}
          </p>
          <h2
            className="text-4xl md:text-5xl font-semibold text-white leading-[1.1] tracking-tight mb-6 transition-all duration-700"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
              transitionDelay: '120ms',
            }}
          >
            {t('stepsSection.title')}
          </h2>
          <p
            className="text-white/60 text-base max-w-xl mx-auto leading-relaxed transition-all duration-700"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: '240ms',
            }}
          >
            {t('stepsSection.subtitle')}
          </p>
        </div>

        {/* Step nav */}
        <div ref={navRef} className="grid lg:grid-cols-5 gap-0 mb-16">
          {steps.map((step, index) => (
            <button
              key={step.number}
              type="button"
              onClick={() => setActiveStep(index)}
              className={`relative flex flex-col items-center py-6 px-4 border-b-2 transition-all duration-500 cursor-pointer ${
                activeStep === index
                  ? 'border-[#F97700] bg-white/5'
                  : 'border-white/10 hover:border-white/30 hover:bg-white/3'
              }`}
              style={{
                opacity: navVisible ? 1 : 0,
                transform: navVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.5s ease, transform 0.5s ease, border-color 0.3s, background-color 0.3s`,
                transitionDelay: `${index * 80}ms`,
              }}
            >
              <span
                className={`text-2xl font-semibold mb-2 transition-colors duration-300 ${
                  activeStep === index ? 'text-[#F97700]' : 'text-white/30'
                }`}
              >
                {step.number}
              </span>
              <span
                className={`text-xs font-medium text-center leading-snug transition-colors duration-300 ${
                  activeStep === index ? 'text-white' : 'text-white/40'
                }`}
              >
                {step.title}
              </span>
            </button>
          ))}
        </div>

        {/* Active Step Detail */}
        {steps[activeStep] && (
          <div
            ref={detailRef}
            key={activeStep}
            className="grid lg:grid-cols-2 gap-12 bg-white/5 border border-white/10 p-10 lg:p-14 transition-all duration-500"
            style={{
              opacity: detailVisible ? 1 : 0,
              transform: detailVisible ? 'translateY(0)' : 'translateY(16px)',
              animation: 'stepFadeIn 0.4s ease-out forwards',
            }}
          >
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-5xl font-semibold text-[#F97700]/30">
                  {steps[activeStep].number}
                </span>
                <h3 className="text-2xl font-semibold text-white tracking-tight">
                  {steps[activeStep].title}
                </h3>
              </div>
              <p className="text-white/70 leading-relaxed mb-8 text-base">
                {steps[activeStep].description}
              </p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-[#F97700]">
                    <i className="ri-time-line"></i>
                  </span>
                  <span className="text-white/50 uppercase tracking-wider text-xs">
                    {t('stepsSection.durationLabel')}:
                  </span>
                  <span className="text-white/80 text-xs">{steps[activeStep].duration}</span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <span className="text-[#F97700] mt-0.5">
                    <i className="ri-file-list-3-line"></i>
                  </span>
                  <span className="text-white/50 uppercase tracking-wider text-xs mt-0.5">
                    {t('stepsSection.deliverableLabel')}:
                  </span>
                  <span className="text-white/80 text-xs leading-relaxed">{steps[activeStep].deliverable}</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-[#F97700] uppercase tracking-wider mb-5">
                {t('stepsSection.activitiesLabel')}
              </p>
              <ul className="space-y-4">
                {steps[activeStep].details.map((detail, idx) => (
                  <li key={idx} className="flex items-start gap-4">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-[#F97700]">
                      <i className="ri-checkbox-circle-line text-base"></i>
                    </span>
                    <span className="text-white/70 text-sm leading-relaxed">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Navigation dots */}
        <div className="flex justify-center gap-2 mt-8">
          {steps.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveStep(i)}
              className={`transition-all duration-300 cursor-pointer ${
                activeStep === i
                  ? 'w-8 h-1.5 bg-[#F97700]'
                  : 'w-1.5 h-1.5 rounded-full bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes stepFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
